/**
 * Utility functions for handling candidate stage and reopened stage logic
 */

interface CandidateProfile {
  reopened_stages?: number[] | null;
  reopened_sections?: string | null;  // JSON string from API
  reopened_at?: string | null;
  review_status?: string | null;
}

interface Candidate {
  profile: CandidateProfile;
}

/**
 * Parse reopened sections from JSON string to array
 * @param sections - The reopened sections (can be JSON string or null)
 * @returns Array of section names or null
 */
export function parseReopenedSections(sections: string | null | undefined): string[] | null {
  if (!sections) return null;
  
  try {
    const parsed = typeof sections === 'string' ? JSON.parse(sections) : sections;
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    console.error('Error parsing reopened sections:', error);
    return null;
  }
}

/**
 * Check if ANY stage is reopened
 * @param candidate - The candidate object
 * @returns true if any stage is reopened
 */
export function hasReopenedStages(candidate: Candidate): boolean {
  return candidate.profile.reopened_stages !== null &&
         candidate.profile.reopened_stages !== undefined &&
         candidate.profile.reopened_stages.length > 0;
}

/**
 * Check if SPECIFIC stage is reopened
 * @param candidate - The candidate object
 * @param stageNumber - The stage number to check (1, 2, or 3)
 * @returns true if the specific stage is reopened
 */
export function isStageReopened(candidate: Candidate, stageNumber: number): boolean {
  return candidate.profile.reopened_stages?.includes(stageNumber) || false;
}

/**
 * Get all reopened information
 * @param candidate - The candidate object
 * @returns Object containing reopened information or null if no stages are reopened
 */
export function getReopenedInfo(candidate: Candidate) {
  if (!hasReopenedStages(candidate)) {
    return null;
  }
  
  return {
    stages: candidate.profile.reopened_stages,        // [1, 2]
    sections: parseReopenedSections(candidate.profile.reopened_sections),    // ["personal_details", ...]
    reopenedAt: candidate.profile.reopened_at,        // "2026-02-25T10:30:00Z"
    status: candidate.profile.review_status          // "reopened"
  };
}

/**
 * Check if candidate resubmitted (completed reopened stages)
 * @param candidate - The candidate object
 * @returns true if the candidate has resubmitted after reopening
 */
export function hasResubmitted(candidate: Candidate): boolean {
  return candidate.profile.review_status === 'waiting' &&
         candidate.profile.reopened_stages === null;
}